/**
 * Comprehensive Admin Notification System
 * Supports: Browser notifications, Toast notifications, Sound alerts
 */

// Request browser notification permission
export const requestNotificationPermission = async () => {
  if (!('Notification' in window)) {
    console.warn('This browser does not support notifications');
    return false;
  }

  if (Notification.permission === 'granted') {
    return true;
  }

  if (Notification.permission !== 'denied') {
    const permission = await Notification.requestPermission();
    return permission === 'granted';
  }

  return false;
};

// Show browser notification
export const showBrowserNotification = (title, options = {}) => {
  if (!('Notification' in window) || Notification.permission !== 'granted') {
    return null;
  }

  const defaultOptions = {
    icon: '/favicon.ico',
    badge: '/favicon.ico',
    tag: 'admin-notification',
    requireInteraction: true, // Default to requiring interaction
    vibrate: [200, 100, 200], // Vibrate pattern for mobile devices
    ...options,
  };

  const notification = new Notification(title, defaultOptions);

  // Handle click - focus window
  notification.onclick = () => {
    window.focus();
    // Don't auto-close - let user close it manually
  };

  return notification;
};

// Store active sound loops
let activeSoundLoops = new Map();

// Global audio context (reused to avoid suspension issues)
let globalAudioContext = null;

// Get or create audio context
export const getAudioContext = async () => {
  if (!globalAudioContext) {
    globalAudioContext = new (window.AudioContext || window.webkitAudioContext)();
  }
  
  // Resume if suspended (required by modern browsers)
  if (globalAudioContext.state === 'suspended') {
    try {
      await globalAudioContext.resume();
    } catch (e) {
      console.warn('Could not resume audio context:', e);
    }
  }
  
  return globalAudioContext;
};

// Play notification sound - LOUD and persistent
export const playNotificationSound = async (type = 'default', notificationId = null, loopUntilSeen = false) => {
  try {
    // Stop any existing sound for this notification
    if (notificationId && activeSoundLoops.has(notificationId)) {
      stopNotificationSound(notificationId);
    }

    const audioContext = await getAudioContext();
    
    // Different sounds for different notification types - MUCH LOUDER
    const sounds = {
      default: {
        frequencies: [800, 1000],
        duration: 0.5,
        volume: 0.7, // Much louder!
      },
      urgent: {
        frequencies: [600, 800, 1000, 1200],
        duration: 0.6,
        volume: 0.8, // Very loud!
      },
      success: {
        frequencies: [523, 659, 784],
        duration: 0.5,
        volume: 0.6,
      },
      error: {
        frequencies: [400, 300, 200],
        duration: 0.5,
        volume: 0.7,
      },
    };

    const soundConfig = sounds[type] || sounds.default;
    const duration = soundConfig.duration;
    const volume = soundConfig.volume;

    const playSound = () => {
      const now = audioContext.currentTime;
      soundConfig.frequencies.forEach((freq, index) => {
        const osc = audioContext.createOscillator();
        const gain = audioContext.createGain();

        osc.type = 'sine';
        osc.connect(gain);
        gain.connect(audioContext.destination);

        const startTime = now + index * 0.05;
        osc.frequency.setValueAtTime(freq, startTime);
        gain.gain.setValueAtTime(0, startTime);
        gain.gain.linearRampToValueAtTime(volume, startTime + 0.01);
        gain.gain.exponentialRampToValueAtTime(0.01, startTime + duration);

        osc.start(startTime);
        osc.stop(startTime + duration);
      });
    };

    // Play initial sound
    playSound();

    // If looping until seen, set up interval
    if (loopUntilSeen && notificationId) {
      const intervalId = setInterval(() => {
        playSound();
      }, duration * 1000 + 200); // Play every duration + small gap

      activeSoundLoops.set(notificationId, {
        intervalId,
        audioContext,
        stop: () => {
          clearInterval(intervalId);
          activeSoundLoops.delete(notificationId);
        },
      });
    }

    return notificationId ? activeSoundLoops.get(notificationId) : null;
  } catch (error) {
    console.error('Failed to play notification sound:', error);
    // Try fallback: create new audio context
    try {
      globalAudioContext = null;
      const audioContext = await getAudioContext();
      console.log('Audio context recreated successfully');
    } catch (e) {
      console.error('Could not recreate audio context:', e);
    }
    return null;
  }
};

// Stop notification sound
export const stopNotificationSound = (notificationId) => {
  if (activeSoundLoops.has(notificationId)) {
    const soundLoop = activeSoundLoops.get(notificationId);
    soundLoop.stop();
  }
};

// Stop all notification sounds
export const stopAllNotificationSounds = () => {
  activeSoundLoops.forEach((soundLoop) => soundLoop.stop());
  activeSoundLoops.clear();
};

// Comprehensive notification function
export const notifyAdmin = async ({
  title,
  message,
  type = 'info', // 'info', 'success', 'warning', 'error', 'urgent'
  sound = true,
  browser = true,
  requireInteraction = true, // Default to true - requires interaction
  loopUntilSeen = true, // Loop sound until seen
  onClick = null,
}) => {
  const notificationId = Date.now() + Math.random();

  // Play sound notification - LOUD and looping until seen
  if (sound) {
    const soundType = type === 'urgent' ? 'urgent' : type === 'error' ? 'error' : type === 'success' ? 'success' : 'default';
    // Use async/await to ensure audio context is ready
    playNotificationSound(soundType, notificationId, loopUntilSeen).catch(err => {
      console.error('Error playing notification sound:', err);
    });
  }

  // Show browser notification (if permission granted) - ALWAYS require interaction for orders
  if (browser && 'Notification' in window && Notification.permission === 'granted') {
    const notification = showBrowserNotification(title, {
      body: message,
      requireInteraction: true, // Always require interaction for important notifications
      icon: '/favicon.ico',
      badge: '/favicon.ico',
      tag: `order-${notificationId}`, // Unique tag to prevent duplicates
    });

    if (onClick && notification) {
      notification.onclick = () => {
        // Stop sound when clicked
        stopNotificationSound(notificationId);
        if (onClick) onClick();
        window.focus();
        notification.close();
      };
    }

    // Stop sound when notification is closed
    notification.addEventListener('close', () => {
      stopNotificationSound(notificationId);
    });
  }

  // Return notification data for toast/UI notifications
  return {
    id: notificationId,
    title,
    message,
    type,
    timestamp: new Date(),
    seen: false,
    soundLoopId: notificationId,
  };
};

// Notification types for different events
export const NotificationTypes = {
  NEW_ORDER: {
    title: '🛒 New Order Received!',
    message: (orderId) => `Order ${orderId} has been placed - Check it now!`,
    type: 'success',
    sound: true,
    browser: true,
    requireInteraction: true,
    loopUntilSeen: true,
  },
  URGENT_ORDER: {
    title: '🚨 URGENT: High Value Order!',
    message: (orderId, amount) => `Order ${orderId} - ${amount} ر.ق - Action Required!`,
    type: 'urgent',
    sound: true,
    browser: true,
    requireInteraction: true,
    loopUntilSeen: true,
  },
  LOW_STOCK: {
    title: 'Low Stock Alert',
    message: (productName) => `${productName} is running low on stock`,
    type: 'warning',
    sound: false,
    browser: true,
  },
  PAYMENT_RECEIVED: {
    title: 'Payment Received',
    message: (orderId) => `Payment confirmed for order ${orderId}`,
    type: 'success',
    sound: true,
    browser: true,
  },
  ORDER_CANCELLED: {
    title: 'Order Cancelled',
    message: (orderId) => `Order ${orderId} has been cancelled`,
    type: 'warning',
    sound: true,
    browser: false,
  },
  SYSTEM_ERROR: {
    title: 'System Error',
    message: (error) => `Error: ${error}`,
    type: 'error',
    sound: true,
    browser: true,
    requireInteraction: true,
  },
};

// Helper to send notification based on type
export const sendNotification = async (notificationType, data, customOptions = {}) => {
  const config = NotificationTypes[notificationType];
  if (!config) {
    console.error(`Unknown notification type: ${notificationType}`);
    return;
  }

  const title = typeof config.title === 'function' ? config.title(data) : config.title;
  const message = typeof config.message === 'function' ? config.message(data) : config.message;

  return notifyAdmin({
    title,
    message,
    type: config.type,
    sound: config.sound,
    browser: config.browser,
    requireInteraction: config.requireInteraction,
    ...customOptions,
  });
};

